import type { RefObject } from 'react';
import { gsap, useGSAP } from '@/lib/gsap';
import { hideRevealTargets } from './hidden-states';
import { useReducedMotion } from './use-reduced-motion';

export type TimelineBuilder = (tl: gsap.core.Timeline, q: gsap.utils.SelectorFunc) => void;

export interface RevealOnEnterOptions {
  /**
   * ScrollTrigger start: where the section's top must reach in the viewport for the reveal
   * to play. 'top bottom' plays as soon as the section enters (or immediately if already in
   * view at mount, which is what the hero wants).
   */
  start?: string;
}

/**
 * The timeline plays once, in real time, when the section scrolls into view; positions and
 * durations in the builder are seconds. Wrapped in gsap.context via useGSAP so everything
 * reverts on unmount. Under reduced motion nothing is hidden and nothing plays.
 */
export function useRevealOnEnter(
  ref: RefObject<HTMLElement>,
  build: TimelineBuilder,
  { start = 'top 72%' }: RevealOnEnterOptions = {},
): void {
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || reduced) return;

      const q = gsap.utils.selector(el);
      hideRevealTargets(q);

      const tl = gsap.timeline({
        defaults: { ease: 'power3.out' },
        // No `id` on purpose: scrollToSection resolves ScrollTriggers by section id and scrolls
        // to their start, which here would be a point 72% down the viewport, not the section.
        scrollTrigger: { trigger: el, start, once: true },
      });
      build(tl, q);
    },
    // revertOnUpdate: a live change of the motion preference must revert the previous run
    // (hidden states, timeline, ScrollTrigger) before this re-runs, or a section already shown
    // snaps hidden and replays, and a stale trigger keeps animating under reduced motion.
    { scope: ref, dependencies: [reduced], revertOnUpdate: true },
  );
}
